import { useState, useEffect, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import api from '../../../services/api';
import {
  Paper,
  IconButton,
  Text,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Select,
  MenuItem,
  Div,
  Spacer,
  Divider
} from '../../core';
import { SecctionContainer, SeccionHeader, AlertDialog } from '../../patterns';
import { NotificationNav } from '../../patterns/NotificationNav';
import { EditIcon } from '../../core/Icons';
import { AppContext } from '../../../contexts/AppContext';
import { LessonPaginator } from '../../patterns/lesson/LessonPaginator';

const STATUS_LABELS = {
  draft: 'Rascunho',
  scheduled: 'Agendada',
  sending: 'Enviando',
  sent: 'Enviada',
  failed: 'Falhou',
  cancelled: 'Cancelada'
};

const MESSAGE_TYPE_LABELS = {
  content: 'Conteúdo',
  reengagement: 'Reengajamento',
  operational: 'Operacional'
};

export default function NotificationsList() {
  const { setLoading } = useContext(AppContext);
  const [notifications, setNotifications] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    count: 0,
    sort: 'desc',
    status: '',
    messageType: '',
    sortField: 'createdAt'
  });

  const [alert, setAlert] = useState({
    show: false,
    errors: [{ message: 'Ocorreu um erro' }]
  });

  const buildQueryParam = useCallback((localPagination) => {
    const parts = [];
    if (localPagination.status) {
      parts.push(`status:like:${localPagination.status}`);
    }
    if (localPagination.messageType) {
      parts.push(`messageType:like:${localPagination.messageType}`);
    }
    return parts.length ? `&q=${parts.join(',')}` : '';
  }, []);

  const loadNotifications = useCallback(({ localPagination = pagination }) => {
    setLoading(true);
    const queryParam = buildQueryParam(localPagination);
    const sortPrefix = localPagination.sort === 'desc' ? '-' : '';

    api
      .get(
        `/admin/push-notifications?page=${localPagination.page}&per_page=${localPagination.per_page}&sort=${sortPrefix}${localPagination.sortField}${queryParam}`
      )
      .then((res) => {
        setPagination((prev) => ({
          ...prev,
          count: res.data.pagination?.count || 0
        }));
        setLoading(false);
        setNotifications(res.data.pushnotifications || []);
      })
      .catch((err) => {
        setLoading(false);
        setNotifications([]);
        setAlert({
          errors: err.response?.data?.errors || [{ message: 'Erro ao carregar notificações' }],
          show: true
        });
      });
  }, [setLoading, buildQueryParam, pagination]);

  useEffect(() => {
    let localPagination = window.localStorage.getItem('notificationsPagination');

    if (localPagination) {
      localPagination = JSON.parse(localPagination);
      setPagination(localPagination);
      loadNotifications({ localPagination });
    } else {
      loadNotifications({ localPagination: pagination });
    }

    setFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!firstLoad) {
      window.localStorage.setItem('notificationsPagination', JSON.stringify(pagination));
      loadNotifications({ localPagination: pagination });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.per_page, pagination.sort, pagination.status, pagination.messageType]);

  const history = useHistory();

  const handleEdit = useCallback((id) => history.push(`/app/notifications/edit/${id}`), [history]);
  const handleNew = useCallback(() => history.push('/app/notifications/edit/new'), [history]);
  const handleDetails = useCallback((id) => history.push(`/app/notifications/${id}`), [history]);

  const onPerPageChange = ({ target }) => {
    setPagination({ ...pagination, per_page: target.value, page: 1 });
  };

  const onPageChange = (event, newPage) => {
    setPagination({ ...pagination, page: newPage + 1 });
  };

  const handleSortChange = ({ target }) => {
    setPagination({ ...pagination, sort: target.value });
  };

  const handleStatusChange = ({ target }) => {
    setPagination({ ...pagination, status: target.value, page: 1 });
  };

  const handleMessageTypeChange = ({ target }) => {
    setPagination({ ...pagination, messageType: target.value, page: 1 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  return (
    <SecctionContainer>
      <NotificationNav />
      <SeccionHeader title="Notificações Push" newButtonAction={handleNew} newButton />
      <AlertDialog
        open={alert.show}
        handleClose={() => setAlert({ ...alert, show: false })}
        errors={alert.errors}
      />
      <TableContainer component={Paper}>
        <Div style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Select
            id="status"
            value={pagination.status || ''}
            handleChange={handleStatusChange}
            label="Status"
            name="status"
            style={{ marginLeft: 20, marginRight: 20, marginTop: 20, maxWidth: 200 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="draft">Rascunho</MenuItem>
            <MenuItem value="sent">Enviada</MenuItem>
            <MenuItem value="sending">Enviando</MenuItem>
            <MenuItem value="failed">Falhou</MenuItem>
            <MenuItem value="scheduled">Agendada</MenuItem>
            <MenuItem value="cancelled">Cancelada</MenuItem>
          </Select>
          <Select
            id="messageType"
            value={pagination.messageType || ''}
            handleChange={handleMessageTypeChange}
            label="Tipo"
            name="messageType"
            style={{ marginRight: 20, marginTop: 20, maxWidth: 200 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="content">Conteúdo</MenuItem>
            <MenuItem value="reengagement">Reengajamento</MenuItem>
            <MenuItem value="operational">Operacional</MenuItem>
          </Select>
          <Spacer />
          <Select
            id="sort"
            value={pagination.sort}
            handleChange={handleSortChange}
            label="Ordem"
            name="sort"
            style={{ marginRight: 20, marginTop: 20, maxWidth: 200 }}
          >
            <MenuItem value="desc">Mais recentes</MenuItem>
            <MenuItem value="asc">Mais antigas</MenuItem>
          </Select>
        </Div>
        <LessonPaginator
          pagination={pagination}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />
        <Divider />
        <Table size="medium">
          <TableBody>
            {notifications.map((notification) => (
              <TableRow key={notification.id} hover>
                <TableCell
                  onClick={() => handleDetails(notification.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <Text style={{ fontWeight: 700 }}>
                    {notification.title?.pt || '—'}
                  </Text>
                  <Text style={{ color: '#666', fontSize: '0.875rem' }}>
                    {notification.body?.pt || ''}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text>{MESSAGE_TYPE_LABELS[notification.messageType] || notification.messageType}</Text>
                </TableCell>
                <TableCell>
                  <Text>{STATUS_LABELS[notification.status] || notification.status}</Text>
                  {notification.status === 'sending' && notification.sendProgress && (
                    <Text style={{ color: '#666', fontSize: '0.75rem' }}>
                      {notification.sendProgress.processed}/{notification.sendProgress.total}
                    </Text>
                  )}
                </TableCell>
                <TableCell>
                  <Text style={{ fontSize: '0.875rem' }}>{formatDate(notification.createdAt)}</Text>
                </TableCell>
                <TableCell align="right">
                  {(notification.status === 'draft' || notification.status === 'scheduled') && (
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(notification.id)}
                      title="Editar notificação"
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {notifications.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Text style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                    Nenhuma notificação encontrada
                  </Text>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SecctionContainer>
  );
}
